import { useEffect, useState, useContext } from 'react'
import { useQuery, useApolloClient, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import { Content, ContentHeader, LayoutContext } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { Button } from 'components/v2/Button'
import { RoutesList } from './RoutesList'
import { ServicesList } from './ServicesList'
import { RoutesFilters } from './RoutesFilters'
import { GET_ROUTES } from 'features/Routes/queries-legacy'
import { resolvers as routesResolvers } from 'resolvers/routes'
import { BOOLEAN, stringIsBoolean } from 'utils/boolean'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { canWrite, hasAllAccess } from 'features/Users/utils'
import RoutesPagination from './Pagination'
import { useLogin } from 'features/Login/hooks'
import { usePlace } from 'features/Places/hooks'
import { IRoutesResponse } from 'features/Routes/types'
import { useCompanyAutocomplete } from 'features/Companies/hooks'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'
import { useBusinessSiteAutocomplete } from 'features/BusinessSites/hooks'
import { FormattedDistance } from 'components/FormattedDistance'

const TablesWrapper = styled.div<TSFixMe>`
  display: flex;
  margin: 0 -10px;
  position: relative;
`

const RoutesTable = styled.div<TSFixMe>`
  width: calc(38% - 20px);
  margin: 0 10px;
`

const ServicesTable = styled.div<TSFixMe>`
  position: absolute;
  width: calc(62% - 20px);
  right: 0;
  top: ${(props): TSFixMe => (props.top ? `${props.top}px` : 0)};
  margin: 0 10px;
  padding-bottom: 30px;
`

const Actions = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: 0 -5px;

  > * {
    margin: 0 5px;
  }
`

const RoutesFiltersWrapper = styled.div`
  margin-bottom: 10px;
`

const ServicesCardHeader = styled(Card.Header)`
  background-color: #916ee8;
`

const messages = defineMessages({
  helmet: {
    id: 'routes.helmet',
    defaultMessage: 'Routes | Nexbus Admin'
  }
})

const LIMIT = 30

const getHighlightedRouteIndex = (highlightedRoute: TSFixMe, routes: TSFixMe): TSFixMe => {
  if (highlightedRoute) {
    const routeIds = routes.map(({ id }: TSFixMe): TSFixMe => id)
    const highlightedRouteIndex = routeIds.indexOf(highlightedRoute)

    if (highlightedRouteIndex > -1) {
      return highlightedRouteIndex
    }
  }

  return 0
}

const RoutesView = (): TSFixMe => {
  const intl = useIntl()
  const location = useLocation()
  const login = useLogin()
  const { layout } = useContext(LayoutContext)
  const client = useApolloClient()
  client.addResolvers(routesResolvers)

  const [activeRouteIndex, setActiveRouteIndex] = useState(0)
  const {
    origin = '',
    destination = '',
    onlyActiveServices = '',
    serviceCode = '',
    companyId = '',
    routeName = '',
    page = '1',
    businessClientId = '',
    businessSiteId = ''
  } = getQueries(location.search)
  const originPlace = usePlace(origin, { skip: !origin })
  const destinationPlace = usePlace(destination, { skip: !destination })
  const companyAutocomplete = useCompanyAutocomplete(companyId, { skip: !companyId })
  const routeAutocomplete = {
    id: '',
    name: routeName
  }
  const businessClientAutocomplete = useBusinessClientAutocomplete(businessClientId, { skip: !businessClientId })
  const businessSiteAutocomplete = useBusinessSiteAutocomplete(businessSiteId, { skip: !businessSiteId })

  const currentPage = parseInt(page)
  const filters = {
    route: {
      id: routeAutocomplete?.id || '',
      name: routeAutocomplete?.name || ''
    },
    company: companyAutocomplete,
    origin: originPlace,
    destination: destinationPlace,
    onlyActiveServices: stringIsBoolean(onlyActiveServices) ? onlyActiveServices : BOOLEAN.TRUE,
    serviceCode,
    businessClient: businessClientAutocomplete,
    businessSite: businessSiteAutocomplete
  }

  const filtersApplied = filterEmptyValue({
    isActive: filters.onlyActiveServices === BOOLEAN.TRUE,
    companyID: companyId,
    name: routeName,
    originID: origin,
    destinationID: destination,
    routeServiceCode: isEmpty(serviceCode) ? undefined : serviceCode,
    businessClientID: businessClientId,
    businessSiteID: businessSiteId
  })

  const { loading, error, data, refetch, networkStatus } = useQuery<{ troutes: IRoutesResponse }>(GET_ROUTES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        page: currentPage,
        size: LIMIT
      },
      input: {
        ...filtersApplied
      }
    },
    onCompleted: (data): TSFixMe => {
      const { highlightedRoute } = getQueries(location.search)
      setActiveRouteIndex(getHighlightedRouteIndex(highlightedRoute, data?.troutes?.items))
    }
  })

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
    refetch()
  }, [])

  useEffect(() => {
    if (layout.appContentEl?.current) {
      layout.appContentEl.current.scrollTop = 0
    }
  }, [page])

  if (!hasAllAccess(FEATURES.ROUTE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.ROUTE} />
  }

  const routes = data?.troutes?.items ?? []
  const hasFiltersApplied = !isEmpty(filtersApplied)

  let content
  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="routes.error.description"
            defaultMessage="There was an error while loading your routes"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="routes.error.action" defaultMessage="Reload your routes" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(routes) && hasFiltersApplied) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="routes.empty.filters.description"
            defaultMessage="No routes found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/routes" kind="primary">
            <FormattedMessage id="routes.empty.filters.action" defaultMessage="Reset your filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(routes)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="routes.empty.description" defaultMessage="You don&#39;t have any routes yet" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/routes/new" kind="primary">
            <FormattedMessage id="routes.empty.action" defaultMessage="Add your first route" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <RoutesList routes={routes} activeRouteIndex={activeRouteIndex} setActiveRouteIndex={setActiveRouteIndex} />
        <RoutesPagination
          page={currentPage}
          limit={LIMIT}
          totalItems={data?.troutes?.total || 0}
          currentItemsCount={data?.troutes?.items.length || 0}
        />
      </>
    )
  }

  const route = routes[activeRouteIndex]

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="routes.header.label" defaultMessage="Routes" />
        </Heading.H3>
        <Actions>
          {canWrite(FEATURES.ROUTE, login.accessToken.policies) && (
            <Button as={NavLink} to="/routes/new" kind="primary">
              <FormattedMessage id="routes.header.new" defaultMessage="+ Create route" />
            </Button>
          )}
          {canWrite(FEATURES.SERVICE, login.accessToken.policies) && (
            <Button as={NavLink} to="/services/new" kind="primary">
              <FormattedMessage id="routes.header.services.new" defaultMessage="+ Create service" />
            </Button>
          )}
        </Actions>
      </ContentHeader>
      <section>
        <TablesWrapper>
          <RoutesTable>
            <Card.Container>
              <Card.Header small>
                <FormattedMessage id="routes.list.title" defaultMessage="Routes" />
              </Card.Header>
              <Card.Content noFooter small>
                <RoutesFiltersWrapper>
                  <RoutesFilters filters={filters} />
                </RoutesFiltersWrapper>
                <section>{content}</section>
              </Card.Content>
            </Card.Container>
          </RoutesTable>
          <ServicesTable id="services-list" top={layout.appContentEl?.current?.scrollTop}>
            {!isEmpty(route) && !loading && !error && (
              <Card.Container>
                <ServicesCardHeader small secondary>
                  <FormattedMessage
                    id="routes.services.title"
                    defaultMessage="Services ({origin} - {destination}) ({distance})"
                    values={{
                      origin: route.origin.name,
                      destination: route.destination.name,
                      distance: <FormattedDistance distance={route.polyline?.distance || 0} />
                    }}
                  />
                </ServicesCardHeader>
                <Card.Content noFooter small>
                  <ServicesList route={route} />
                </Card.Content>
              </Card.Container>
            )}
          </ServicesTable>
        </TablesWrapper>
      </section>
    </Content>
  )
}

export default withLastUrl(RoutesView)
